// v2026-06-01c — force rebuild: fix backfill sentinel + skip warm entries
import React from "react";
import ReactDOM from "react-dom/client";
import App from "./App.jsx";
import { OAUTH_MESSAGE_TYPE, OAUTH_STORAGE_KEY, parseOAuthCallback } from "./utils/oauthRelay";
import { parseEarlyBirdShareId } from "./utils/earlyBirdShareHash";

// ── OAuth 弹窗回调中转 ──────────────────────────────────────────────
// 弹窗式 Google 登录（见 src/hooks/useAuth.js signInWithGoogle + 风险清单 §7.18/§7.19）
// 走完 Google 流程后会回到这同一个 redirectTo 地址，但这次加载的是弹窗自己的
// window，不是发起登录的主窗口。这里只负责把 code 传回主窗口再关闭自己——不渲染
// 完整 App、不在弹窗自己的 storage 里做 exchange（exchange 必须用主窗口那份从未
// 被导航打断过的 verifier，弹窗自己刚经历的这趟 Google 跳转本身就是可能触发
// storage 清理的那条链路）。
//
// 之前只用 window.opener 判断"我是不是弹窗"+ postMessage 传回去，但
// accounts.google.com 会带 Cross-Origin-Opener-Policy: same-origin 响应头——
// 弹窗跳过去之后浏览器会永久切断 window.opener（哪怕之后跳回同源页面也恢复不了），
// 导致这里判断成"不是弹窗"从而渲染出一个完整 App（用户会看到主窗口显示未登录、
// 弹窗关掉后也没登上——2026-07-06 实测复现）。改为：只要 URL 带 code/error 参数
// 就认定是弹窗回调，不再依赖 window.opener 是否还活着；relay 双通道发送——
// postMessage（opener 没被切断时能收到）+ localStorage（同源存储，不依赖窗口间
// 脚本引用，主窗口那边用 storage 事件 + 轮询兜底读取，见 utils/oauthRelay.js）。
const callback = parseOAuthCallback(window.location.search);

if (callback) {
  const payload = { type: OAUTH_MESSAGE_TYPE, code: callback.code, error: callback.error };
  try { window.opener && window.opener.postMessage(payload, window.location.origin); } catch {}
  try { localStorage.setItem(OAUTH_STORAGE_KEY, JSON.stringify(payload)); } catch {}
  document.getElementById("root").innerHTML =
    '<div style="display:flex;align-items:center;justify-content:center;height:100vh;font-family:sans-serif;color:#666;font-size:14px;">登录完成，正在关闭…</div>';
  window.close();
} else {
  // ── Early Bird 分享链接：真实路径 → 内部 hash 路由 ──────────────────
  // /earlybird/:id 是给 Twitter/Discord 等链接预览抓取器用的真实路径（服务端
  // proxy/server.js 会在这个路径上动态注入该项目的 OG meta 标签，见其注释）。
  // 抓取器不执行 JS，只读服务端返回的原始 HTML；但真人点开链接后浏览器会正常
  // 加载这个 React app，这里在渲染前把真实路径转成 App 内部认得的 #earlybird/:id
  // hash 路由（App.jsx 里 PipelinePage/EarlyRadarPanel/EarlyBirdTab 三层 state
  // 初始化时都会读这个 hash 展开到正确的 tab + 打开对应弹窗）。
  const pathShareId = parseEarlyBirdShareId(window.location.pathname.slice(1));
  if (pathShareId) {
    window.history.replaceState(null, '', `/#earlybird/${encodeURIComponent(pathShareId)}`);
  }

  const root = ReactDOM.createRoot(document.getElementById("root"));
  root.render(<React.StrictMode><App /></React.StrictMode>);
}
